class NeoPaceSceneGallery extends HTMLElement {
  connectedCallback() {
    this.abortController?.abort();
    this.abortController = new AbortController();
    this.mediaViewport = this.querySelector('[data-media-viewport]');
    this.mediaTrack = this.querySelector('[data-media-track]');
    this.tabsViewport = this.querySelector('[data-tabs-viewport]');
    this.arrows = this.querySelector('[data-arrows]');
    this.status = this.querySelector('[data-scene-status]');
    this.desktopMedia = window.matchMedia('(min-width: 750px)');
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.currentBlockId = null;
    this.userStoppedAutoplay = false;
    this.hoverPaused = false;
    this.focusPaused = false;
    this.isDocumentVisible = document.visibilityState === 'visible';
    this.isIntersecting = false;
    this.pointerStart = null;
    this.interval = Number(this.dataset.interval) || 3000;
    this.designMode = this.dataset.designMode === 'true';
    this.setAttribute('data-enhanced', 'true');
    this.bindEvents();
    this.refresh({ animate: false });
    this.observeVisibility();
  }

  disconnectedCallback() {
    this.stopTimer();
    this.isIntersecting = false;
    this.observer?.disconnect();
    this.resizeObserver?.disconnect();
    if (this.alignFrame) window.cancelAnimationFrame(this.alignFrame);
    this.abortController?.abort();
  }

  bindEvents() {
    const signal = this.abortController.signal;

    this.addEventListener('click', (event) => this.handleClick(event), { signal });
    this.addEventListener('keydown', (event) => this.handleKeydown(event), { signal });
    this.addEventListener('pointerenter', () => {
      this.hoverPaused = true;
      this.stopTimer();
    }, { signal });
    this.addEventListener('pointerleave', () => {
      this.hoverPaused = false;
      this.scheduleTimer();
    }, { signal });
    this.addEventListener('focusin', () => {
      this.focusPaused = true;
      this.stopTimer();
    }, { signal });
    this.addEventListener('focusout', () => {
      queueMicrotask(() => {
        this.focusPaused = this.contains(document.activeElement);
        this.scheduleTimer();
      });
    }, { signal });
    this.mediaViewport?.addEventListener('pointerdown', (event) => this.handlePointerDown(event), { signal });
    this.mediaViewport?.addEventListener('pointerup', (event) => this.handlePointerUp(event), { signal });
    this.mediaViewport?.addEventListener('pointercancel', () => {
      this.pointerStart = null;
    }, { signal });
    this.addEventListener('error', (event) => this.handleImageError(event), { capture: true, signal });
    this.desktopMedia.addEventListener('change', () => this.refresh({ animate: false }), { signal });
    this.reducedMotion.addEventListener('change', () => {
      this.stopTimer();
      this.scheduleTimer();
    }, { signal });
    document.addEventListener('visibilitychange', () => {
      this.isDocumentVisible = document.visibilityState === 'visible';
      if (this.isDocumentVisible) this.scheduleTimer();
      else this.stopTimer();
    }, { signal });
    document.addEventListener('shopify:block:select', (event) => this.handleBlockSelect(event), { signal });

    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(() => this.scheduleAlignment());
      if (this.mediaViewport) this.resizeObserver.observe(this.mediaViewport);
      if (this.tabsViewport) this.resizeObserver.observe(this.tabsViewport);
    }
  }

  observeVisibility() {
    if (!('IntersectionObserver' in window)) return;

    this.observer = new IntersectionObserver((entries) => {
      this.isIntersecting = entries.some((entry) => entry.isIntersecting);
      if (this.isIntersecting) this.scheduleTimer();
      else this.stopTimer();
    }, { threshold: 0.15 });
    this.observer.observe(this);
  }

  get isDesktop() {
    return this.desktopMedia.matches;
  }

  get validItems() {
    const key = this.isDesktop ? 'validDesktop' : 'validMobile';
    return Array.from(this.querySelectorAll('[data-scene-block-id]')).filter((item) => item.dataset[key] === 'true');
  }

  get validTabs() {
    const key = this.isDesktop ? 'validDesktop' : 'validMobile';
    return Array.from(this.querySelectorAll('[data-scene-tab-id]')).filter((tab) => tab.dataset[key] === 'true');
  }

  refresh({ animate }) {
    const items = this.validItems;
    const preserved = items.find((item) => item.dataset.sceneBlockId === this.currentBlockId);
    const target = preserved || items[0];
    const section = this.closest('.neopace-scene-gallery-section');
    const focusedTab = document.activeElement?.closest?.('[data-scene-tab-id]');

    if (!target) {
      this.currentBlockId = null;
      this.stopTimer();
      if (section) section.hidden = !this.designMode;
      return;
    }

    if (section) section.hidden = false;
    this.activate(target.dataset.sceneBlockId, { animate, manual: false, announce: false });
    if (focusedTab && this.contains(focusedTab) && !this.validTabs.includes(focusedTab)) {
      this.validTabs.find((tab) => tab.dataset.sceneTabId === target.dataset.sceneBlockId)?.focus();
    }
  }

  activate(blockId, { animate = true, manual = false, announce = true } = {}) {
    const items = this.validItems;
    const tabs = this.validTabs;
    const target = items.find((item) => item.dataset.sceneBlockId === blockId);
    if (!target) return;

    this.currentBlockId = blockId;
    if (manual) this.userStoppedAutoplay = true;
    this.prioritizeActiveImage(target);

    if (this.arrows && this.arrows.parentElement !== target) {
      target.append(this.arrows);
    }

    for (const item of this.querySelectorAll('[data-scene-block-id]')) {
      const active = item === target;
      item.classList.toggle('is-active-desktop', active && this.isDesktop);
      item.classList.toggle('is-active-mobile', active && !this.isDesktop);
      item.setAttribute('aria-hidden', String(!active));
    }

    for (const tab of this.querySelectorAll('[data-scene-tab-id]')) {
      const active = tab.dataset.sceneTabId === blockId;
      tab.classList.toggle('is-active-desktop', active && this.isDesktop);
      tab.classList.toggle('is-active-mobile', active && !this.isDesktop);
      tab.setAttribute('aria-selected', String(active));
      tab.tabIndex = active ? 0 : -1;
    }

    if (this.arrows) this.arrows.hidden = items.length < 2;
    this.stopTimer();

    this.scheduleAlignment(animate);

    this.updateStatus(blockId, tabs, { announce, manual });

    this.scheduleTimer();
  }

  updateStatus(blockId, tabs, { announce, manual }) {
    if (!announce || !manual || !this.status) return;
    const activeTab = tabs.find((tab) => tab.dataset.sceneTabId === blockId);
    this.status.textContent = activeTab?.textContent?.trim() || '';
  }

  scheduleAlignment(animate = false) {
    if (this.alignFrame) window.cancelAnimationFrame(this.alignFrame);
    this.alignFrame = window.requestAnimationFrame(() => {
      this.alignFrame = window.requestAnimationFrame(() => {
        this.alignFrame = null;
        if (!this.isConnected || !this.currentBlockId) return;
        const item = this.validItems.find((candidate) => candidate.dataset.sceneBlockId === this.currentBlockId);
        const tab = this.validTabs.find((candidate) => candidate.dataset.sceneTabId === this.currentBlockId);
        if (!item) return;
        this.alignItem(item, animate);
        this.alignTab(tab, animate);
      });
    });
  }

  prioritizeActiveImage(item) {
    const end = this.isDesktop ? 'desktop' : 'mobile';
    const image = item.querySelector(`[data-image-end="${end}"]`);
    if (image) image.loading = 'eager';
  }

  alignItem(item, animate) {
    if (!this.mediaViewport) return;
    const left = item.offsetLeft - (this.mediaViewport.clientWidth - item.offsetWidth) / 2;
    const max = Math.max(0, this.mediaViewport.scrollWidth - this.mediaViewport.clientWidth);
    this.mediaViewport.scrollTo({
      left: Math.min(Math.max(left, 0), max),
      behavior: animate && !this.reducedMotion.matches ? 'smooth' : 'auto',
    });
  }

  alignTab(tab, animate) {
    if (!tab || !this.tabsViewport) return;
    const left = tab.offsetLeft + tab.offsetWidth / 2 - this.tabsViewport.clientWidth / 2;
    const max = Math.max(0, this.tabsViewport.scrollWidth - this.tabsViewport.clientWidth);
    this.tabsViewport.scrollTo({
      left: Math.min(Math.max(left, 0), max),
      behavior: animate && !this.reducedMotion.matches ? 'smooth' : 'auto',
    });
  }

  handleClick(event) {
    if (!(event.target instanceof Element)) return;
    const tab = event.target.closest('[data-scene-tab-id]');
    if (tab && this.contains(tab)) {
      this.activate(tab.dataset.sceneTabId, { manual: true });
      return;
    }

    const arrow = event.target.closest('[data-direction]');
    if (arrow && this.contains(arrow)) this.advance(arrow.dataset.direction === 'next' ? 1 : -1, true);
  }

  handleKeydown(event) {
    if (!(event.target instanceof Element)) return;
    const tab = event.target.closest('[data-scene-tab-id]');
    if (!tab || !this.contains(tab)) return;

    const tabs = this.validTabs;
    const currentIndex = tabs.indexOf(tab);
    let targetIndex = null;
    if (event.key === 'ArrowRight') targetIndex = (currentIndex + 1) % tabs.length;
    if (event.key === 'ArrowLeft') targetIndex = (currentIndex - 1 + tabs.length) % tabs.length;
    if (event.key === 'Home') targetIndex = 0;
    if (event.key === 'End') targetIndex = tabs.length - 1;
    if (targetIndex === null) return;

    event.preventDefault();
    const target = tabs[targetIndex];
    this.activate(target.dataset.sceneTabId, { manual: true });
    target.focus();
  }

  handlePointerDown(event) {
    if (this.isDesktop || event.button !== 0 || this.validItems.length < 2) return;
    this.pointerStart = { x: event.clientX, y: event.clientY, id: event.pointerId };
  }

  handlePointerUp(event) {
    if (!this.pointerStart || event.pointerId !== this.pointerStart.id) return;
    const deltaX = event.clientX - this.pointerStart.x;
    const deltaY = event.clientY - this.pointerStart.y;
    this.pointerStart = null;
    if (Math.abs(deltaX) < 40 || Math.abs(deltaX) <= Math.abs(deltaY) * 1.2) return;
    this.advance(deltaX < 0 ? 1 : -1, true);
  }

  handleImageError(event) {
    if (!(event.target instanceof HTMLImageElement)) return;
    const end = event.target.dataset.imageEnd;
    const item = event.target.closest('[data-scene-block-id]');
    if (!item || (end !== 'desktop' && end !== 'mobile')) return;
    item.dataset[end === 'desktop' ? 'validDesktop' : 'validMobile'] = 'false';
    const tab = this.querySelector(`[data-scene-tab-id="${item.dataset.sceneBlockId}"]`);
    if (tab) tab.dataset[end === 'desktop' ? 'validDesktop' : 'validMobile'] = 'false';
    this.refresh({ animate: false });
  }

  handleBlockSelect(event) {
    if (!(event.target instanceof Element) || !this.contains(event.target)) return;
    const block = event.target.closest('[data-scene-block-id]');
    if (!block) return;
    this.activate(block.dataset.sceneBlockId, { animate: true, manual: false, announce: false });
  }

  advance(offset, manual) {
    const items = this.validItems;
    if (items.length < 2) return;
    const index = items.findIndex((item) => item.dataset.sceneBlockId === this.currentBlockId);
    const next = items[(index + offset + items.length) % items.length];
    this.activate(next.dataset.sceneBlockId, { manual, announce: manual });
  }

  canAutoplay() {
    return this.isConnected
      && this.validItems.length > 1
      && !this.userStoppedAutoplay
      && !this.hoverPaused
      && !this.focusPaused
      && this.isIntersecting
      && this.isDocumentVisible
      && !this.reducedMotion.matches;
  }

  scheduleTimer() {
    this.stopTimer();
    if (!this.canAutoplay()) return;
    this.timerId = window.setTimeout(() => this.advance(1, false), this.interval);
  }

  stopTimer() {
    if (!this.timerId) return;
    window.clearTimeout(this.timerId);
    this.timerId = null;
  }
}

if (!customElements.get('neopace-scene-gallery')) {
  customElements.define('neopace-scene-gallery', NeoPaceSceneGallery);
}
